import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSpinner, FaGift, FaEdit, FaSave, FaPercent, FaRupeeSign } from "react-icons/fa";
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function EditOfferPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [offerData, setOfferData] = useState({ name: '', value: '', unit: '', minAmount: '' });
    const [savingOffer, setSavingOffer] = useState(false);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('authToken');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const getOfferData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/single-offer-data/${id}`);
            setOfferData({
                name: response.data.name || '',
                value: response.data.value !== undefined ? String(response.data.value) : '',
                unit: response.data.unit || '',
                minAmount: response.data.minAmount !== undefined ? String(response.data.minAmount) : '',
            });
        } catch (error) {
            handleApiError(error);
            setTimeout(() => navigate('/admin/offers'), 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        setOfferData({
            ...offerData,
            [event.target.name]: event.target.value,
        });
    };

    const validateFields = () => {
        if (!offerData.name.trim()) {
            alert('Offer name is required');
            return false;
        }
        if (!String(offerData.value).trim() || isNaN(Number(offerData.value))) {
            alert('Offer value is required and must be a valid number');
            return false;
        }
        if (!offerData.unit.trim()) {
            alert('Offer unit is required');
            return false;
        }
        if (offerData.minAmount && isNaN(Number(offerData.minAmount))) {
            alert('Minimum amount must be a valid number');
            return false;
        }
        return true;
    };

    const handleUpdateOffer = async () => {
        if (!validateFields()) return;

        try {
            setSavingOffer(true);
            const response = await axios.patch(`${API_BASE_URL}/update-offer/${id}`, offerData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            navigate('/admin/offers');
        } catch (error) {
            handleApiError(error);
        } finally {
            setSavingOffer(false);
        }
    };

    const handleBackButtonClick = () => {
        navigate('/admin/offers');
    };

    const getUnitIcon = (unit) => {
        return unit === 'Percent' ? <FaPercent /> : <FaRupeeSign />;
    };

    const getUnitColor = (unit) => {
        return unit === 'Percent' ? 'text-success' : 'text-primary';
    };

    useEffect(() => {
        getOfferData();
    }, [id]);

    if (loading) {
        return (
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-1">EDIT OFFER</h4>
                            <p className="text-muted p-0 m-0">Update offer information and details</p>
                        </div>
                    </div>
                </div>
                <AdminPageLoader message="Loading offer details..." />
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">EDIT OFFER</h4>
                                <p className="text-muted p-0 m-0">Update offer information and details</p>
                            </div>
                            <button 
                                className="btn theme-btn-yellow btn-sm rounded-0"
                                onClick={handleBackButtonClick}
                                title="Back to Offers"
                            >
                                <FaArrowLeft className="me-2" />
                                Back to Offers
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row justify-content-center mt-4">
                    <div className="col-lg-6 col-md-8 col-sm-10">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white border-0">
                                <h5 className="mb-0">
                                    <FaEdit className="theme-edit-icon me-2" />
                                    Offer Information
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Offer Name</label>
                                            <input
                                                type="text"
                                                className="form-control rounded-0"
                                                placeholder="Enter offer name"
                                                name="name"
                                                value={offerData.name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Offer Value</label>
                                            <input
                                                type="number"
                                                min={0}
                                                className="form-control rounded-0"
                                                placeholder="Enter offer value"
                                                name="value"
                                                value={offerData.value}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Unit</label>
                                            <select
                                                className="form-select rounded-0"
                                                name="unit"
                                                value={offerData.unit}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Unit</option>
                                                <option value="Percent">Percent (%)</option>
                                                <option value="Rupees">Rupees (₹)</option>
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold">Minimum Amount</label>
                                            <input
                                                type="number"
                                                min={0}
                                                className="form-control rounded-0"
                                                placeholder="Enter minimum order amount for offer"
                                                name="minAmount"
                                                value={offerData.minAmount}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <button
                                            className="btn theme-btn-blue rounded-0 w-100"
                                            disabled={savingOffer}
                                            onClick={handleUpdateOffer}
                                        >
                                            {savingOffer ? (
                                                <>
                                                    <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                    Updating Offer...
                                                </>
                                            ) : (
                                                <>
                                                    <FaSave className="me-2" />
                                                    Update Offer
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    
                                    <div className="col-md-6">
                                        <div className="text-center">
                                            <label className="form-label fw-bold">Offer Preview</label>
                                            <div className="border rounded p-4">
                                                <div className="d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
                                                    <div className="text-center">
                                                        <FaGift style={{ color: '#1a237e' }} className="fs-1 mb-3" />
                                                        <h5 className="mb-2">{offerData.name || 'Offer Name'}</h5>
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <strong style={{ color: '#1a237e' }} className="fs-4 me-2">
                                                                {offerData.value || '0'}
                                                            </strong>
                                                            {offerData.unit && (
                                                                <span
                                                                    className="badge fs-6"
                                                                    style={{
                                                                        background: offerData.unit === 'Percent' ? '#ffff01' : '#1a237e',
                                                                        color: offerData.unit === 'Percent' ? '#1a237e' : '#ffff01',
                                                                        border: '1.5px solid #1a237e',
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {getUnitIcon(offerData.unit)} {offerData.unit}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {!offerData.name && (
                                                            <small className="text-muted">Enter offer details to see preview</small>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

export default EditOfferPage;
